/** @jsxImportSource @emotion/react */

import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { v4 as uuid4 } from 'uuid';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import ImageBlock from '@component/common/block/image-block';
import Gap from '@component/common/gap';
import Spacer from '@component/common/spacer';
import WorkbookTemplate from '@component/iyagigo/common/workbook-template';
import TextButton from '@design-system/text-button';
import Chip from '@design-system/chip';
import Canvas from '@design-system/canvas';
import ProgressBar from '@design-system/progress-bar';
import otterTestImage from '@asset/image/otter-test.webp';
import vocaCardImage from '@asset/image/voca-card.webp';
import incorrectPageImage from '@asset/image/incorrect-page.webp';
import { ETheme } from '@datatype/type';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { actions as navigationActions, DEFAULT_IYAGIGO_SUB_MENU } from '@store/slice/navigation-slice';
import { EVocabularyBookNoteType, TIyagigoSubMenu, VOCABULARY_BOOK_LEVEL_LABEL, VOCABULARY_BOOK_LEVEL_ORDER } from '@datatype/iyagigo/type';
import { EApiLanguageCode, EApiVocabularyBookCategory, EApiVocabularyBookLevel, IVocabularyBook } from '@datatype/iyagigo/api-type';
import { useVocabularyBookProgressQuery } from '@hook/iyagigo/useApi';
import { useCurrentSubMenu } from '@hook/iyagigo/useSync';
import { useMySubscriptionQuery } from '@hook/billing/useApi';
import { EApiSubscriptionStatus } from '@datatype/billing/api-type';
import { GUEST_VOCABULARY_BOOK_FREE_ORDER, MEMBER_VOCABULARY_BOOK_FREE_ORDER } from '@constant/iyagigo/variable';
import Blur from '@component/common/blur';

import { IYAGIGO_PATH, createVocabularyBookPath } from '@constant/iyagigo/route-path';
import { actions as vocabularyBookActions } from '@store/slice/vocabulary-book-slice';
import { IS_DEMO } from '@constant/variable';

const BannerContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: flex-start;
  gap: ${size.MARGIN_2};
  width: 100%;
`;

const BannerContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 8px;
`;

const flicker = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1) rotate(-1deg);
  }
  20% {
    opacity: 0.85;
    transform: scale(1.05) rotate(1deg);
  }
  40% {
    opacity: 1;
    transform: scale(0.98) rotate(-2deg);
  }
  60% {
    opacity: 0.9;
    transform: scale(1.03) rotate(2deg);
  }
  80% {
    opacity: 1;
    transform: scale(1) rotate(-1deg);
  }
`;

const FireContainer = styled.div`
  width: 100%;

  & svg {
    transform-origin: center bottom;
    animation: ${flicker} 1.2s infinite ease-in-out;
  }
`;

const ReviewButtonContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const ReviewCardInner = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const StudiedMetricContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 5px;
  width: 100%;
`;

const VocabularyBookBundleContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_1};
`;

const VocabularyBookCardFirstRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  width: 100%;
`;

const VocabularyBookCardLockRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 5px;
  background-color: ${color.WHITE_1};
  padding: 4px 10px;
  border-radius: 999px;
  box-shadow: ${color.SURFACE_SHADOW};
`;

const VocabularyBookCardContainer = styled.div<{ $isLocked: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  padding: 12px 14px;
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.WHITE_1};
  box-shadow: ${color.SURFACE_SHADOW};
  cursor: ${({ $isLocked }) => ($isLocked ? 'not-allowed' : 'pointer')};
  opacity: ${({ $isLocked }) => ($isLocked ? 0.6 : 1)};
  transition: transform 0.15s ease;
`;

const VocabularyBookCardSecondRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 5px;
  min-height: 25px;
`;

const GradeContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 5px;
`;

const CurrentVocabularyBookCardContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

// 복습 탭 본문 컨테이너 — 학습 탭의 현재 단어집 카드와 동일한 톤(흰 배경 + shadow).
const ReviewHighlightContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.WHITE_1};
  box-shadow: ${color.SURFACE_SHADOW};
`;

// 현재 단어집 카드 — 반복 학습과 대등하게 자체 컨테이너(흰 배경 + shadow)로 분리
const CurrentBookHighlightContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.WHITE_1};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const VocabularyBookProgressContainer = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-flow: column nowrap;
`;

const VocabularyBookRecord = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const ProgressBarContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const TEST_DESCRIPTION_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: { alignX: 'left' },
} as const;

const REVIEW_BUTTON_IMAGE_DESIGN = { image: { height: '100%' }, box: { height: '35px', width: '50px' } } as const;
const REVIEW_BUTTON_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
} as const;

// 시험단어집(시험준비) 숨김 — 카테고리 축 대신 단어집/단어노트 축으로 재편.
// const VOCABULARY_BOOK_TYPE_TABS: { id: string; name: string }[] = [
//   { id: 'vocabulary-book-study', name: '일반학습' },
//   { id: 'vocabulary-book-test', name: '시험준비' },
// ];

type TVocabularyBookTabId = NonNullable<TIyagigoSubMenu['vocabulary-book']>;

// 상단 탭 = 사이드탭 submenu(IG_VOCABULARY_BOOK_MENUS)와 동일 축. subMenu state로 동기화.
// 단어집(새로 배울 단어) / 단어노트(반복 큐 + 저장한/모르는 단어)
const VOCABULARY_BOOK_TABS: { id: TVocabularyBookTabId; name: string }[] = [
  { id: 'vocabulary-book-study', name: '단어집' },
  { id: 'vocabulary-book-note', name: '단어노트' },
];

const VOCABULARY_BOOK_SUB_MENU_IDS = ['vocabulary-book-study', 'vocabulary-book-note'] as const;

const VocabularyBookHome: React.FC = () => {
  const dispatch = useTypedDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  const vocabularyBookSubMenu = useCurrentSubMenu(
    VOCABULARY_BOOK_SUB_MENU_IDS,
    DEFAULT_IYAGIGO_SUB_MENU['vocabulary-book'] as 'vocabulary-book-study',
  );
  // 시험단어집(TEST) 미노출 — 카테고리는 STUDY 고정. subMenu는 학습/복습 탭 축으로만 사용.
  const vocabularyBookType = EApiVocabularyBookCategory.STUDY;
  const selectedTabId: TVocabularyBookTabId = vocabularyBookSubMenu;

  const handleVocabularyBookTabClick = (id: TVocabularyBookTabId) => {
    dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'vocabulary-book', subMenu: id }));
  };
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const apiLanguageCode =
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE;

  const subscriptionQuery = useMySubscriptionQuery();
  const isSubscriber = subscriptionQuery.data?.data?.status === EApiSubscriptionStatus.ACTIVE;

  // Redux (studentReducer)에서 (언어 × 카테고리) 4개 필드 중 매칭 (cloze 패턴)
  const currentEnglishTestVocabularyBookLevel = useTypedSelector(
    (state) => state.rootReducer?.studentReducer?.currentEnglishTestVocabularyBookLevel,
  );
  const currentEnglishStudyVocabularyBookLevel = useTypedSelector(
    (state) => state.rootReducer?.studentReducer?.currentEnglishStudyVocabularyBookLevel,
  );
  const currentJapaneseTestVocabularyBookLevel = useTypedSelector(
    (state) => state.rootReducer?.studentReducer?.currentJapaneseTestVocabularyBookLevel,
  );
  const currentJapaneseStudyVocabularyBookLevel = useTypedSelector(
    (state) => state.rootReducer?.studentReducer?.currentJapaneseStudyVocabularyBookLevel,
  );
  // 시험단어집(TEST) 미노출 — STUDY 레벨만 사용. TEST 재노출 시 아래 분기 부활.
  // const currentLevel =
  //   apiLanguageCode === EApiLanguageCode.ENGLISH
  //     ? vocabularyBookType === EApiVocabularyBookCategory.TEST
  //       ? currentEnglishTestVocabularyBookLevel
  //       : currentEnglishStudyVocabularyBookLevel
  //     : vocabularyBookType === EApiVocabularyBookCategory.TEST
  //       ? currentJapaneseTestVocabularyBookLevel
  //       : currentJapaneseStudyVocabularyBookLevel;
  const currentLevel =
    apiLanguageCode === EApiLanguageCode.ENGLISH
      ? currentEnglishStudyVocabularyBookLevel
      : currentJapaneseStudyVocabularyBookLevel;
  const currentLevelLabel = currentLevel ? VOCABULARY_BOOK_LEVEL_LABEL[currentLevel] : '';

  // 해당 레벨 책별 진도 + 오늘 학습할 책 key (백엔드 1 API 통합)
  const { data: progressResponse } = useVocabularyBookProgressQuery(
    {
      languageCode: apiLanguageCode,
      category: vocabularyBookType,
      level: currentLevel as EApiVocabularyBookLevel,
    },
    { enabled: !!currentLevel },
  );
  const apiBookProgresses = useMemo(() => progressResponse?.data?.books ?? [], [progressResponse]);
  const bookProgresses = useMemo(
    () =>
      IS_DEMO
        ? apiBookProgresses.map((progress) => {
            if (progress.book.order === 1) {
              return {
                ...progress,
                studiedCount: progress.book.itemCount,
                hardCount: 0,
                normalCount: 2,
                easyCount: Math.max(progress.book.itemCount - 2, 0),
              };
            }
            if (progress.book.order === 2) {
              return {
                ...progress,
                studiedCount: progress.book.itemCount,
                hardCount: 1,
                normalCount: 1,
                easyCount: Math.max(progress.book.itemCount - 2, 0),
              };
            }
            return { ...progress, studiedCount: 0, hardCount: 0, normalCount: 0, easyCount: 0 };
          })
        : apiBookProgresses,
    [apiBookProgresses],
  );
  const vocabularyBooks = useMemo<IVocabularyBook[]>(() => bookProgresses.map((p) => p.book), [bookProgresses]);
  const currentVocabularyBookKey = IS_DEMO
    ? (bookProgresses.find((progress) => progress.book.order === 3)?.book.key ?? null)
    : (progressResponse?.data?.currentVocabularyBookKey ?? null);
  const currentProgress = bookProgresses.find((p) => p.book.key === currentVocabularyBookKey) ?? null;
  const currentBook = currentProgress?.book ?? null;

  // 레벨 종합 통계 (책별 progress 합계)
  const focusMastery = useMemo(() => {
    const totalWords = bookProgresses.reduce((sum, p) => sum + p.book.itemCount, 0);
    const studiedWords = bookProgresses.reduce((sum, p) => sum + p.studiedCount, 0);
    const totalBooks = bookProgresses.length;
    const completedBooks = bookProgresses.filter(
      (p) => p.studiedCount >= p.book.itemCount && p.book.itemCount > 0,
    ).length;
    const wordPercentage = totalWords > 0 ? Math.round((studiedWords / totalWords) * 100) : 0;
    return { totalWords, studiedWords, totalBooks, completedBooks, wordPercentage };
  }, [bookProgresses]);

  // 카테고리 탭 제거로 미사용. subMenu는 기본값(vocabulary-book-study) 고정.
  // const handleVocabularyBookTypeTabClick = (id: string) => {
  //   dispatch(
  //     navigationActions.storeIyagigoSubMenu({
  //       mainMenu: 'vocabulary-book',
  //       subMenu: id as 'vocabulary-book-test' | 'vocabulary-book-study',
  //     }),
  //   );
  // };

  // 단어집 선택 옵션은 모두 보여주고, 저장 시점에 Premium 접근을 확인한다.
  const handleChangeVocabularyBookClick = () => {
    dispatch(
      modalActions.showDialogModal({
        type: 'vocabulary-book-study-configuration',
        data: null,
      }),
    );
  };

  // 책 잠금 여부: 운영(isPlan) OR 비회원 무료 한도 초과 OR 무료회원 무료 한도 초과
  const isBookLocked = (book: IVocabularyBook): boolean => {
    if (book.isPlan) return true;
    if (!isAuthenticated && book.order > GUEST_VOCABULARY_BOOK_FREE_ORDER) return true;
    if (isAuthenticated && !isSubscriber && book.order > MEMBER_VOCABULARY_BOOK_FREE_ORDER) return true;
    return false;
  };

  // 비회원이면서 회원만으로 풀 수 있는 잠금(2~5일차)이면 회원가입 유도, 그 외(6일차+, 회원 비구독자)는 Plus⁺
  const getLockedCopy = (book: IVocabularyBook): string => {
    if (!isAuthenticated && book.order <= MEMBER_VOCABULARY_BOOK_FREE_ORDER) {
      return '회원가입하고 잠금해제';
    }
    return 'Plus⁺구독하고 잠금해제';
  };

  const handleVocabularyBookClick = (book: IVocabularyBook) => {
    if (isBookLocked(book)) {
      // 비회원 2~5일차: 회원가입만 하면 풀 수 있는 구간 → signup-wall
      // 그 외(비회원 6일차+, 무료회원 6일차+): Plus⁺ 구독 필요 → 구독 페이지로 직접 이동
      //   (비회원이 구독 누르면 subscription 페이지에서 회원가입 유도)
      if (!isAuthenticated && book.order <= MEMBER_VOCABULARY_BOOK_FREE_ORDER) {
        dispatch(modalActions.showWallToast({ type: 'signup-wall', data: { trigger: 'access' } }));
      } else {
        dispatch(navigationActions.storeIyagigoSubMenu({ mainMenu: 'hub', subMenu: 'subscription' }));
        navigate(IYAGIGO_PATH.HUB_SUBSCRIPTION_PATH);
      }
      return;
    }
    dispatch(vocabularyBookActions.configureVocabularyBookStudy());
    navigate(`${createVocabularyBookPath('study')}&vocabularyBookKey=${book.key}`);
  };

  const handleNoteClick = (type: EVocabularyBookNoteType) => {
    if (!isAuthenticated) {
      dispatch(modalActions.showWallToast({ type: 'signup-wall', data: { trigger: 'access' } }));
      return;
    }
    if (type === EVocabularyBookNoteType.INCORRECT) {
      dispatch(vocabularyBookActions.configureVocabularyBookIncorrectNote({}));
    } else {
      dispatch(vocabularyBookActions.configureVocabularyBookBookmarkNote({}));
    }
    navigate(createVocabularyBookPath('note'));
  };

  const handleReviewClick = () => {
    if (!isAuthenticated) {
      dispatch(modalActions.showWallToast({ type: 'signup-wall', data: { trigger: 'access' } }));
      return;
    }
    dispatch(vocabularyBookActions.configureVocabularyBookReview());
    navigate(createVocabularyBookPath('study'));
  };

  const reviewCount = progressResponse?.data?.reviewCount ?? 0;

  const VocabularyBookCards = bookProgresses.map(({ book, studiedCount, hardCount, normalCount, easyCount }) => {
    const dayLabel = `${book.order}일차`;
    const ratio = book.itemCount > 0 ? studiedCount / book.itemCount : 0;
    const isCompleted = studiedCount >= book.itemCount && book.itemCount > 0;
    const actionText = isCompleted ? '복습하기' : studiedCount > 0 ? '이어하기' : '시작하기';
    const locked = isBookLocked(book);
    return (
      <VocabularyBookCardContainer key={uuid4()} $isLocked={locked} onClick={() => handleVocabularyBookClick(book)}>
        <Blur
          blur={locked}
          width="100%"
          icon={
            <VocabularyBookCardLockRow>
              <IconBlock icon="lock" design={{ icon: { kind: 'outlined', size: 'small', color: color.GRAY_1 } }} />
              <TextBlock
                text={getLockedCopy(book)}
                design={{ text: { size: 'small', color: color.GRAY_1, bold: false }, box: {} }}
              />
            </VocabularyBookCardLockRow>
          }>
          <VocabularyBookCardFirstRow>
            <TextBlock
              text={dayLabel}
              design={{
                text: { size: 'medium', color: color.BLACK_2, bold: true },
                box: { width: '60px' },
              }}
            />
            <VocabularyBookProgressContainer>
              <ProgressBar ratio={ratio} theme={ETheme.BROWN} width="100%" height="6px" />
            </VocabularyBookProgressContainer>
            <TextBlock
              text={actionText}
              design={{
                text: { size: 'small', color: color.BROWN_1, bold: true },
                box: { width: '60px', alignX: 'right' },
              }}
            />
          </VocabularyBookCardFirstRow>
          <VocabularyBookCardSecondRow>
            <GradeContainer>
              {studiedCount > 0 ? (
                <>
                  <Chip text={`몰라요 ${hardCount}`} size="tiny" usage="static" kind="outlined" theme={ETheme.RED} />
                  <Chip
                    text={`헷갈려요 ${normalCount}`}
                    size="tiny"
                    usage="static"
                    kind="outlined"
                    theme={ETheme.YELLOW}
                  />
                  <Chip text={`알아요 ${easyCount}`} size="tiny" usage="static" kind="outlined" theme={ETheme.BLUE} />
                </>
              ) : (
                <TextBlock
                  text="아직 학습 전이에요"
                  design={{ text: { size: 'tiny', color: color.GRAY_1 }, box: {} }}
                />
              )}
            </GradeContainer>
            <TextBlock
              text={`${studiedCount} / ${book.itemCount}`}
              design={{ text: { size: 'tiny', color: color.GRAY_1 }, box: {} }}
            />
          </VocabularyBookCardSecondRow>
        </Blur>
      </VocabularyBookCardContainer>
    );
  });

  const CurrentVocabularyBookCard = () => {
    const studied = currentProgress?.studiedCount ?? 0;
    const total = currentBook?.itemCount ?? 0;
    const isCompleted = !!currentBook && total > 0 && studied >= total;
    // 다음 미완료 책 = currentBook 이후 order 중 studiedCount < itemCount 인 첫 책
    const nextBook = currentBook
      ? bookProgresses
        .filter(({ book, studiedCount }) => book.order > currentBook.order && studiedCount < book.itemCount)
        .sort((a, b) => a.book.order - b.book.order)[0]?.book
      : null;
    const buttonText = (() => {
      if (studied === 0) return '시작하기';
      if (!isCompleted) return '이어하기';
      if (nextBook) return '다음 단어집';
      return '복습하기';
    })();
    const handleClick = () => {
      if (isCompleted && nextBook) {
        handleVocabularyBookClick(nextBook);
        return;
      }
      if (currentBook) handleVocabularyBookClick(currentBook);
    };
    return (
      <CurrentVocabularyBookCardContainer>
        <VocabularyBookCardFirstRow>
          <TextBlock
            text={currentBook ? `${currentBook.order}일차` : ''}
            design={{
              text: { size: 'medium', color: color.BLACK_2, bold: true },
              box: { width: '60px' },
            }}
          />
          <VocabularyBookProgressContainer>
            <ProgressBar ratio={total > 0 ? studied / total : 0} theme={ETheme.BROWN} width="100%" height="6px" />
          </VocabularyBookProgressContainer>
          <TextBlock
            text={(() => {
              if (isCompleted) return '완료';
              if (studied > 0) return `${studied} / ${total}`;
              return '시작 전';
            })()}
            design={{
              text: { size: 'small', color: color.GRAY_1 },
              box: { width: '50px', alignX: 'right' },
            }}
          />
        </VocabularyBookCardFirstRow>
        <Gap height={size.MARGIN_1} />
        <TextButton
          text={buttonText}
          theme={ETheme.BROWN}
          size="medium"
          shape='pill'
          width="100%"
          handleClick={handleClick}
        />
      </CurrentVocabularyBookCardContainer>
    );
  };

  // 복습 탭 본문 — 반복할 단어 수 + grade 분포(몰라요/헷갈려요/알아요) + 시작하기
  const ReviewVocabularyBookCard = () => {
    const reviewHardCount = progressResponse?.data?.reviewHardCount ?? 0;
    const reviewNormalCount = progressResponse?.data?.reviewNormalCount ?? 0;
    const reviewEasyCount = progressResponse?.data?.reviewEasyCount ?? 0;
    return (
      <CurrentVocabularyBookCardContainer>
        <VocabularyBookCardFirstRow>
          <TextBlock
            text={`반복할 단어 <strong style="color: ${color.BLACK_4};">${reviewCount}개</strong>가 있어요`}
            design={{
              text: { size: 'medium', color: color.BLACK_4 },
              box: { alignX: 'left' },
            }}
          />
        </VocabularyBookCardFirstRow>
        <VocabularyBookCardSecondRow>
          <GradeContainer>
            <Chip text={`몰라요 ${reviewHardCount}`} size="tiny" usage="static" kind="outlined" theme={ETheme.RED} />
            <Chip
              text={`헷갈려요 ${reviewNormalCount}`}
              size="tiny"
              usage="static"
              kind="outlined"
              theme={ETheme.YELLOW}
            />
            <Chip text={`알아요 ${reviewEasyCount}`} size="tiny" usage="static" kind="outlined" theme={ETheme.BLUE} />
          </GradeContainer>
        </VocabularyBookCardSecondRow>
        <Gap height={size.MARGIN_1} />
        <TextButton
          text="시작하기"
          theme={ETheme.BROWN}
          size="medium"
          shape='pill'
          width="100%"
          handleClick={handleReviewClick}
        />
      </CurrentVocabularyBookCardContainer>
    );
  };

  const isNoteTab = selectedTabId === 'vocabulary-book-note';

  // 단어집 탭 — 신규 학습 (현재 단어집 + 단어집 진도)
  const VocabularyBookContent = (
    <>
      <BannerContainer>
        <ImageBlock src={otterTestImage} alt="otter" design={{ image: { height: '80px' } }} />
        <BannerContent>
          <TextBlock text="현재 단어집" design={{ text: { size: 'medium', color: color.BLACK_2 } }} />
          <TextBlock text={currentLevelLabel} design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }} />
          <IconTextBlock
            icon={'edit'}
            text="단어집 변경하기"
            arrangement="text-icon"
            design={{
              text: { size: 'tiny', color: color.GRAY_1, underline: false },
              icon: { size: 'tiny', color: color.GRAY_1, kind: 'outlined' },
              box: { event: { cursor: 'pointer' }, gap: '2px' },
            }}
            click={{
              handler: handleChangeVocabularyBookClick,
            }}
          />
        </BannerContent>
      </BannerContainer>
      <Gap height={size.MARGIN_1} />
      <Canvas theme={ETheme.BROWN} shape={'square'} width="100%">
        <TextBlock
          text={`매일 <strong style="color: #A26939;">1일차씩</strong> <strong style="color: #A26939;">단어</strong>와 친해져요`}
          design={TEST_DESCRIPTION_DESIGN}
        />
        <Gap height={size.MARGIN_1} />
        <StudiedMetricContainer>
          <TextBlock text="외운 단어" design={{ text: { size: 'small', color: color.BROWN_1, bold: true }, box: {} }} />
          <TextBlock
            text={`${focusMastery.studiedWords} / ${focusMastery.totalWords}`}
            design={{ text: { size: 'small', color: color.GRAY_1 } }}
          />
        </StudiedMetricContainer>
        <Gap height={size.MARGIN_1} />
        <ProgressBar ratio={focusMastery.wordPercentage / 100} theme={ETheme.BROWN} width="100%" height="6px" />
      </Canvas>
      <Gap height={size.MARGIN_2} />
      {currentProgress && (
        <>
          <TextBlock
            text="오늘의 학습"
            design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
          />
          <Gap height={size.MARGIN_1} />
          <CurrentBookHighlightContainer>
            <IconTextBlock
              icon="book"
              text="현재 단어집"
              arrangement="icon-text"
              design={{
                text: { size: 'medium', color: color.BLACK_2, bold: true },
                icon: { size: 'small', color: color.BLACK_2, kind: 'outlined' },
                box: { alignX: 'left', gap: '4px' },
              }}
            />
            <Gap height={size.MARGIN_1} />
            <CurrentVocabularyBookCard />
          </CurrentBookHighlightContainer>
        </>
      )}
      <Gap height={size.MARGIN_3} />
      <VocabularyBookRecord>
        <TextBlock
          text={'단어집 진도'}
          design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: {} }}
        />
        <ProgressBarContainer>
          <ProgressBar
            ratio={focusMastery.totalBooks > 0 ? focusMastery.completedBooks / focusMastery.totalBooks : 0}
            theme={ETheme.BROWN}
            width="100%"
            height="6px"
          />
        </ProgressBarContainer>
        <TextBlock
          text={`${focusMastery.completedBooks} / ${focusMastery.totalBooks}일차`}
          design={{ text: { size: 'tiny', color: color.GRAY_1 }, box: {} }}
        />
      </VocabularyBookRecord>
      <Gap height={size.MARGIN_1} />
      {vocabularyBooks.length === 0 ? (
        <TextBlock text="아직 단어집이 없어요" design={{ text: { size: 'small', color: color.GRAY_1 } }} />
      ) : (
        <VocabularyBookBundleContainer>{VocabularyBookCards}</VocabularyBookBundleContainer>
      )}
    </>
  );

  // 단어노트 탭 — 반복 학습(SRS 큐) + 모아둔 단어(저장한/모르는 단어 노트)
  const VocabularyNoteContent = (
    <>
      <BannerContainer>
        <ImageBlock src={otterTestImage} alt="otter" design={{ image: { height: '80px' } }} />
        <BannerContent>
          <TextBlock text="단어노트" design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }} />
        </BannerContent>
      </BannerContainer>
      <Gap height={size.MARGIN_1} />
      <Canvas theme={ETheme.BROWN} shape={'square'} width="100%">
        <TextBlock
          text={`그동안 <strong style="color: #A26939;">쌓아둔 단어</strong>를 다시 꺼내 볼수록\n더 <strong style="color: #A26939;">오래 기억</strong>에 남아요`}
          design={TEST_DESCRIPTION_DESIGN}
        />
      </Canvas>
      <Gap height={size.MARGIN_2} />
      <TextBlock
        text="반복 학습"
        design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
      />
      <Gap height={size.MARGIN_1} />
      {reviewCount > 0 ? (
        <ReviewHighlightContainer>
          <ReviewVocabularyBookCard />
        </ReviewHighlightContainer>
      ) : (
        <TextBlock
          text="반복할 단어가 없어요"
          design={{ text: { size: 'small', color: color.GRAY_1 }, box: { alignX: 'left' } }}
        />
      )}
      <Gap height={size.MARGIN_3} />
      {/* 소제목은 '모아둔 단어' — 탭 이름(단어노트)과 중복을 피하면서 '반복 학습'(자동 큐)과 대비 */}
      <TextBlock
        text="모아둔 단어"
        design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
      />
      <Gap height={size.MARGIN_1} />
      <ReviewButtonContainer>
        <Canvas
          theme={ETheme.BLUE}
          shadow={true}
          shape={'round'}
          width="100%"
          handleClick={() => handleNoteClick(EVocabularyBookNoteType.BOOKMARK)}>
          <ReviewCardInner>
            <ImageBlock src={vocaCardImage} alt="저장한 단어" design={REVIEW_BUTTON_IMAGE_DESIGN} />
            <TextBlock text="저장한 단어 보기" design={REVIEW_BUTTON_TEXT_DESIGN} />
          </ReviewCardInner>
        </Canvas>
        <Canvas
          theme={ETheme.RED}
          shadow={true}
          shape={'round'}
          width="100%"
          handleClick={() => handleNoteClick(EVocabularyBookNoteType.INCORRECT)}>
          <ReviewCardInner>
            <ImageBlock src={incorrectPageImage} alt="모르는 단어" design={REVIEW_BUTTON_IMAGE_DESIGN} />
            <TextBlock text="모르는 단어 보기" design={REVIEW_BUTTON_TEXT_DESIGN} />
          </ReviewCardInner>
        </Canvas>
      </ReviewButtonContainer>
      <Spacer />
    </>
  );

  return (
    <WorkbookTemplate
      tabs={VOCABULARY_BOOK_TABS}
      selectedTabId={selectedTabId}
      onTabClick={(id) => handleVocabularyBookTabClick(id as TVocabularyBookTabId)}>
      {!isNoteTab && VocabularyBookContent}
      {isNoteTab && VocabularyNoteContent}
    </WorkbookTemplate>
  );
};

export default VocabularyBookHome;
